import React, { FC, useCallback, useEffect, useState, useRef } from "react";
import api from '../../api';
import { CircularProgress, LinearProgress, Stack, Button, Snackbar, Alert, AlertColor } from "@mui/material";
import { connect, useDispatch } from "react-redux";
import { updateUser, updateImageCaption, addAgencyImage, deleteAgencyImage, deleteAgencyVideo } from "../../redux/reducers/userReducer";
import SimpleImageSlider from "react-simple-image-slider";
import Textarea from "shared/Textarea/Textarea";
import './style.css';
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ImagePreviewModal from "./ImagePreviewModal";

const MediaSection = ({
  user,
  entity,
  entity_id,
  openDeleteModal,
}) => {
  const dispatch = useDispatch();
  const videoRef = useRef(null);

  const [videoSrc, setVideoSrc] = useState(null);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [pendingVideoChanges, setPendingVideoChanges] = useState(false);
  const [videoKey, setVideoKey] = useState(0);
  const [showVideo, setShowVideo] = useState(false);

  const [images, setImages] = useState([]);
  const [imagesUploading, setImagesUploading] = useState([]);
  const [currentImage, setCurrentImage] = useState(null);
  const [currentIndex, setIndex] = useState(0);
  const [isImageLoading, setImageLoading] = useState(false);
  const [pendingImageChanges, setPendingImageChanges] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ open: false, imageId: null });
  const [previewImage, setPreviewImage] = useState(false);
  const [showPreviewModal, setPreviewModal] = useState(false);

  const [openSnackbar, setOpenSnackbar] = useState({
    open: false,
    type: 'success' as AlertColor,
    message: ''
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [sliderKey, setSliderKey] = useState(0);

  const processImages = useCallback(() => {
    if (user?.company?.images) {
      const imgs = user.company.images.map((i) => {
        return {
          id: i.id,
          isLoading: false,
          name: i.url,
          caption: i.caption,
          url: `${api.getUploadUrl()}/${i.url}`,
        };
      });
      setImages(imgs);
      setTimeout(() => {
        setSliderKey(prev => prev + 1); // change key to force remount
        if (currentIndex + 1 > imgs.length) {
          setIndex(0);
        }
      }, 100);
    }
  }, [user?.company?.images]);

  useEffect(() => {
    if (user?.company?.showroom_video?.url) {
      const timestamp = Date.now();
      setVideoSrc(`${api.getUploadUrl()}/${user.company.showroom_video.url}?t=${timestamp}`);
      setShowVideo(true);
    } else {
      setVideoSrc(null);
      setShowVideo(false);
    }
  }, [user?.company?.showroom_video]);

  useEffect(() => {
    if (videoSrc && videoRef.current && showVideo) {
      const video = videoRef.current;

      setTimeout(() => {
        video.load();
      }, 200);
    }
  }, [videoSrc, videoKey, showVideo]);

  useEffect(() => {
    processImages();
  }, [processImages]);

  useEffect(() => {
    setCurrentImage(images && images.length ? images[currentIndex] : null);
  }, [images, currentIndex]);

  const isFileExists = (f) => {
    for (let i in images) {
      if (images[i].name === f.name) return true;
    }
    return false;
  };

  const validateVideoFile = (file) => {
    if (!file) return false;

    const allowedFormats = ['mp4', 'mov'];
    const maxSize = 200 * 1024 * 1024;

    const fileExtension = file.name.split('.').pop().toLowerCase();

    if (!allowedFormats.includes(fileExtension)) {
      setOpenSnackbar({
        open: true,
        type: 'error' as AlertColor,
        message: 'Please upload a video file in MP4 or MOV format.'
      });
      return false;
    }

    if (file.size > maxSize) {
      setOpenSnackbar({
        open: true,
        type: 'error' as AlertColor,
        message: 'Video file size must be less than 200MB.'
      });
      return false;
    }

    return true;
  };

  const handleVideoUpload = (e) => {
    if (videoSrc) {
      setOpenSnackbar({
        open: true,
        type: 'error' as AlertColor,
        message: 'Please delete the current video before uploading a new one.'
      });
      return;
    }

    const file = e.target.files[0];
    if (!file) return;

    if (!validateVideoFile(file)) {
      e.target.value = '';
      return;
    }

    setIsVideoLoading(true);
    let formData = new FormData();
    formData.append("file", file);
    formData.append("key", 'showroom_video');
    formData.append("entity", entity);
    formData.append("entity_id", entity_id);

    api.uploadAttachment(formData, (res) => {
      if (res && res.data) {
        handleVideoUploadSuccess(res.data);
        setPendingVideoChanges(true);
        setHasUnsavedChanges(true);
        setOpenSnackbar({
          open: true,
          type: 'success' as AlertColor,
          message: 'Video uploaded successfully!'
        });
      } else {
        setIsVideoLoading(false);
        setOpenSnackbar({
          open: true,
          type: 'error' as AlertColor,
          message: 'Video upload failed. Please try again.'
        });
      }
    });

    e.target.value = '';
  };

  const handleVideoUploadSuccess = (file) => {
    setShowVideo(false);
    setVideoSrc(null);

    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(7);
    file.url = `${file.url}?t=${timestamp}&r=${randomId}&v=${videoKey + 1}`;

    setIsVideoLoading(false);

    dispatch(updateUser({
      company: {
        ...user.company,
        showroom_video: file
      }
    }));

    setTimeout(() => {
      setVideoSrc(`${api.getUploadUrl()}/${file.url}`);
      setVideoKey(prev => prev + 1);
      setShowVideo(true);
    }, 300);
  };

  const handleImageUpload = (e) => {
    let files = e.target.files;
    if (!files || files.length === 0) return;

    if (files.length + images.length > 8) {
      setOpenSnackbar({ open: true, type: 'error' as AlertColor, message: "You can only upload up to " + (8 - images.length) + " images." });
      e.target.value = "";
      return;
    }

    let uploadsInProgress = [...imagesUploading];
    const uploadedImages = [];

    setImageLoading(true);

    for (let i = 0; i < files.length; i++) {
      let file = files[i];
      if (!file || !file.name) continue;
      if (isFileExists(file)) continue;

      const uploadingFile = {
        id: `temp-${Date.now()}-${i}`,
        isLoading: true,
        name: file.name,
        url: '',
      };

      uploadsInProgress.push(uploadingFile);
      setImagesUploading(uploadsInProgress);

      let formData = new FormData();
      formData.append("file", file);
      formData.append("key", 'brand_image');
      formData.append("entity", entity);
      formData.append("entity_id", entity_id);

      api.uploadAttachment(formData, (res) => {
        setImagesUploading(prev => prev.filter(item => item.name !== file.name));
        uploadedImages.push(res.data);

        if (uploadedImages.length === files.length) {
          const tempImages = [...images, ...uploadedImages];
          dispatch(addAgencyImage(uploadedImages));
          setHasUnsavedChanges(true);
          setIndex(tempImages.length - 1);
          setImageLoading(false);
        }
      });
    }
  };

  const handleArrowClick = (e) => {
    const max = images.length - 1;
    let i = 0;
    if (e) {
      i = currentIndex !== max ? (currentIndex + 1) : 0;
    } else {
      i = currentIndex !== 0 ? (currentIndex - 1) : max;
    }

    setIndex(i);
  };

  const handleBulletClick = (i) => {
    setIndex(i);
  };

  const handleImageClick = (e) => {
    setPreviewImage(images[e]?.url ?? '')
    setPreviewModal(true)
  }

  const handlePreviewClose = () => {
    setPreviewModal(false)
  }

  const handleCaptionChange = (e) => {
    const newCaption = e.target.value;

    setImages((prevImages) =>
      prevImages.map((image, index) =>
        index === currentIndex ? { ...image, caption: newCaption } : image
      )
    );

    setPendingImageChanges(true);
    setHasUnsavedChanges(true);
  };

  const saveChanges = () => {
    if (pendingImageChanges) {
      images.forEach((image, index) => {
        api.updateAttachment(
          { id: image.id, caption: image.caption },
          (res) => {
            dispatch(updateImageCaption({ index, caption: image.caption }));
          }
        );
      });

      setPendingImageChanges(false);
      setOpenSnackbar({
        open: true,
        type: "success" as AlertColor,
        message: "Media changes saved successfully!",
      });
    }

    setHasUnsavedChanges(false);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar({ open: false, type: openSnackbar.type, message: openSnackbar.message });
  };

  const DeleteIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
      <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
    </svg>
  );

  return (
    <div id="media" className="mt-25">
      <div className="mb-18 flex justify-between">
        <span className="block mt-2 text-neutral-500 dark:text-neutral-400">
          Upload a Video of Your Showroom (Max: 2 minutes)
        </span>
        {user?.company?.showroom_video && (
          <div onClick={() => openDeleteModal(user.company.showroom_video.id, 'video')} className="cursor-pointer">
            <DeleteIcon />
          </div>
        )}
      </div>
      <div className="mb-18">
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
        <div className="space-y-8">
          <div className="mt-5">
            {isVideoLoading ? (
              <div className="flex">
                <div className="flex-1"></div>
                <div className="text-center">
                  <CircularProgress size={100} />
                  <div className="mt-4">Uploading...</div>
                </div>
                <div className="flex-1"></div>
              </div>
            ) : (
              <>
                {showVideo && videoSrc && (
                  <div className="relative">
                    <video
                      ref={videoRef}
                      key={`video-${videoKey}`}
                      className="m-0"
                      width="100%"
                      height={300}
                      controls
                      preload="auto"
                      playsInline
                      src={videoSrc}
                    />
                  </div>
                )}
                {(!showVideo || !videoSrc) && (
                  <div className="mt-5">
                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-neutral-300 dark:border-neutral-6000 border-dashed rounded-md">
                      <div className="space-y-1 text-center">
                        <svg
                          className="mx-auto h-12 w-12 text-neutral-400"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 48 48"
                          aria-hidden="true"
                        >
                          <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          ></path>
                        </svg>
                        <div className="flex text-sm text-neutral-6000 dark:text-neutral-300">
                          <label
                            htmlFor="file-upload-showroom_video"
                            className="relative cursor-pointer rounded-md font-medium text-primary-6000 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
                          >
                            <span>Upload Video</span>
                            <input
                              id="file-upload-showroom_video"
                              name="files"
                              type="file"
                              multiple={false}
                              accept=".mp4,.mov,video/mp4,video/quicktime"
                              className="sr-only"
                              onChange={handleVideoUpload}
                            />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">
                          MOV, MP4 up to 200MB
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="border-b border-neutral-200 dark:border-neutral-700 w-14 mt-10 mb-10 justify-self-center"></div>

      <div className="mt-18 flex justify-between">
        <span className="block text-neutral-500 dark:text-neutral-400">
          Pictures of Your Showroom and Team (Max: 8 images)
        </span>
        {currentImage && (
          <div onClick={() => openDeleteModal(currentImage.id, 'image')} className="cursor-pointer">
            <DeleteIcon />
          </div>
        )}
      </div>

      <div className="mt-18">
        <div className="space-y-8">
          <div>
            <div className="mt-5">
              <div className="relative">
                {images.length > 0 && (
                  <>
                    <SimpleImageSlider
                      key={sliderKey}
                      style={{ width: '100% !important' }}
                      width={'100%'}
                      height={300}
                      images={images}
                      showBullets={!isImageLoading}
                      showNavs={!isImageLoading}
                      onClickNav={handleArrowClick}
                      onClickBullets={handleBulletClick}
                      startIndex={currentIndex}
                      onClick={handleImageClick}
                    />
                    {currentImage && (
                      <>
                        <Textarea
                          className="mt-6"
                          placeholder="Tell us what this photo is about"
                          rows={2}
                          name='caption'
                          value={currentImage?.caption ?? ""}
                          onChange={handleCaptionChange}
                        />
                        {/* {isImageLoading && <LinearProgress />} */}
                      </>
                    )}
                  </>
                )}
              </div>
              {/* <Stack
                direction={'row'}
                spacing={1}
                useFlexGap
                sx={{ flexWrap: 'wrap' }}
              >
                {imagesUploading && imagesUploading.length > 0 && imagesUploading.map((r, i) => {
                  if (r.isLoading) {
                    return (
                      <div key={i} className="image-item-box space-y-1 text-center">
                        <div>{r.name}</div>
                        <LinearProgress sx={{ bottom: '0' }} />
                      </div>
                    );
                  }
                  return null;
                })}
              </Stack> */}
            </div>
            {isImageLoading &&
              <div className="flex mt-5">
                <div className="flex-1"></div>
                <div className="text-center">
                  <CircularProgress size={100} />
                  <div className="mt-4">Uploading...</div>
                </div>
                <div className="flex-1"></div>
              </div>
            }
            {images.length < 8 && !isImageLoading &&
              <div className="mt-5">
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-neutral-300 dark:border-neutral-6000 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-neutral-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      ></path>
                    </svg>
                    <div className="flex text-sm text-neutral-6000 dark:text-neutral-300">
                      <label
                        htmlFor="file-upload-brand_image"
                        className="relative cursor-pointer rounded-md font-medium text-primary-6000 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
                      >
                        <span>Upload files</span>
                        <input
                          id="file-upload-brand_image"
                          name="files"
                          type="file"
                          multiple={true}
                          accept="image/*"
                          className="sr-only"
                          onChange={handleImageUpload}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      PNG, JPG, GIF up to 10MB
                    </p>
                  </div>
                </div>
              </div>
            }
          </div>
        </div>
      </div>

      <div className="mt-8 flex justify-end">
        <ButtonPrimary onClick={saveChanges}>Save Changes{pendingImageChanges ? ' (Image Captions)' : ''}</ButtonPrimary>
      </div>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={openSnackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={openSnackbar.type}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {openSnackbar.message}
        </Alert>
      </Snackbar>
      {showPreviewModal && (
        <ImagePreviewModal
          imageUrl={previewImage}
          open={showPreviewModal}
          onClose={() => handlePreviewClose()}
        />
      )}
    </div>
  );
};

export default connect(null, { updateUser, updateImageCaption, addAgencyImage, deleteAgencyVideo, deleteAgencyImage })(MediaSection);